import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import authApi from '../api/auth/authApi';
import userApi from '../api/user/userApi';
import { eventApi } from '../api/events/eventApi';
import { STORAGE_KEYS } from '../api/config';

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [events, setEvents] = useState([]);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const storedUser = localStorage.getItem(STORAGE_KEYS.USER);
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);

    if (storedUser && token) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        if (Array.isArray(userData.events) && userData.events.length > 0) {
          setEvents(userData.events);
          
          const savedEventId = localStorage.getItem('selectedEventId');
          const savedDefaultId = localStorage.getItem('defaultEventId');
          const targetId = savedEventId || savedDefaultId || userData.default_event_id;

          let activeEvent = null;
          if (targetId) {
            activeEvent = userData.events.find(
              e => e.id === parseInt(targetId) || e.event_address === targetId
            );
          }
          setCurrentEvent(activeEvent || userData.events[0] || null);
        }
      } catch (err) {
        console.error('Error parsing stored user:', err);
      }
    }
    setLoading(false);
  }, []);

  const fetchProfile = async (id) => {
    try {
      const response = await userApi.getProfile(id || user?.id);
      if (response.success || response.status === 800) {
        let updatedEvents = [];
        try {
          const eventsResponse = await eventApi.getUserEvents();
          updatedEvents = eventsResponse?.data?.data || eventsResponse?.data || [];
        } catch (eventErr) {
          console.error('Error fetching events during profile refresh:', eventErr);
        }

        const updatedUser = { ...user, ...response.data };
        if (updatedEvents.length > 0) {
          updatedUser.events = updatedEvents;
          setEvents(updatedEvents);

          const savedEventId = localStorage.getItem('selectedEventId');
          const currentExist = updatedEvents.find(
            e => e.id === parseInt(savedEventId) || e.event_address === savedEventId
          );
          if (!currentExist && updatedEvents.length > 0) {
            const nextEvent = updatedEvents[0];
            setCurrentEvent(nextEvent);
            localStorage.setItem('selectedEventId', nextEvent.id?.toString());
            localStorage.setItem(STORAGE_KEYS.UID, nextEvent.event_address);
            localStorage.setItem(STORAGE_KEYS.EVENT_ID, nextEvent.id);
          } else if (currentExist) {
            setCurrentEvent(currentExist);
          }
        }

        setUser(updatedUser);
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(updatedUser));
        return response.data;
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
    }
  };

  const login = async (credentials) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authApi.login(credentials);
      
      if (response.success || response.status === 800) {
        const { token, user_data, events: userEvents } = response.data;
        
        // Clear any old user's leftover event storage keys
        localStorage.removeItem('selectedEventId');
        localStorage.removeItem('defaultEventId');
        localStorage.removeItem('defaultEvent');
        localStorage.removeItem('selectedEvent');

        localStorage.setItem(STORAGE_KEYS.TOKEN, token);
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user_data));
        localStorage.setItem(STORAGE_KEYS.USER_ID, user_data.id);
        
        if (Array.isArray(userEvents) && userEvents.length > 0) {
          // Find default event if set
          const defaultEvt = userEvents.find(e => 
            e.id === user_data.default_event_id || 
            e.is_default === 1 || 
            e.is_default === true
          ) || userEvents[0];

          localStorage.setItem(STORAGE_KEYS.UID, defaultEvt.event_address);
          localStorage.setItem(STORAGE_KEYS.EVENT_ID, defaultEvt.id);
          localStorage.setItem('selectedEventId', defaultEvt.id?.toString());
          if (user_data.default_event_id || defaultEvt.is_default) {
            localStorage.setItem('defaultEventId', defaultEvt.id?.toString());
            localStorage.setItem('defaultEvent', JSON.stringify(defaultEvt));
          }

          setEvents(userEvents);
          setCurrentEvent(defaultEvt);
        } else {
          setEvents([]);
          setCurrentEvent(null);
        }
        
        setUser(user_data);
        toast.success('Login successful!');
        
        navigate('/dashboard');
        return response;
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (err) {
      const message = err.message || 'Login failed';
      setError(message);
      toast.error(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Purge all keys so multi-account sessions do not bleed into each other
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
      localStorage.removeItem(STORAGE_KEYS.UID);
      localStorage.removeItem(STORAGE_KEYS.USER_ID);
      localStorage.removeItem(STORAGE_KEYS.EVENT_ID);
      localStorage.removeItem('selectedEventId');
      localStorage.removeItem('defaultEventId');
      localStorage.removeItem('defaultEvent');
      localStorage.removeItem('selectedEvent');

      setUser(null);
      setEvents([]);
      setCurrentEvent(null);
      toast.info('Logged out successfully');
      navigate('/login');
    }
  };

  const setDefaultEvent = async (eventOrId) => {
    try {
      const targetId = typeof eventOrId === 'object' ? eventOrId?.id : eventOrId;
      const targetEvent = events.find(e => e.id === parseInt(targetId)) || (typeof eventOrId === 'object' ? eventOrId : null);

      if (!targetId) throw new Error('Invalid event ID');

      // 1. Call API POST /api/set-default-event
      const response = await eventApi.setDefaultEvent(targetId);

      // 2. Update Local Storage for active user session
      localStorage.setItem('defaultEventId', targetId.toString());
      if (targetEvent) {
        localStorage.setItem('defaultEvent', JSON.stringify(targetEvent));
        localStorage.setItem('selectedEventId', targetId.toString());
        localStorage.setItem(STORAGE_KEYS.UID, targetEvent.event_address);
        localStorage.setItem(STORAGE_KEYS.EVENT_ID, targetId);
        setCurrentEvent(targetEvent);
      }

      // 3. Update User State & Events List
      if (Array.isArray(events)) {
        const updatedEvents = events.map(e => ({
          ...e,
          is_default: String(e.id) === String(targetId) ? 1 : 0
        }));
        setEvents(updatedEvents);
      }

      if (user) {
        const updatedUser = { ...user, default_event_id: targetId };
        setUser(updatedUser);
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(updatedUser));
      }

      toast.success('Shughuli ya msingi (Default) imesasishwa kikamilifu!');
      return response;
    } catch (err) {
      console.error('Error setting default event:', err);
      toast.error(err?.response?.data?.message || err?.message || 'Imeshindwa kuweka shughuli ya msingi');
      throw err;
    }
  };

  const changePassword = async (passwordData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authApi.changePassword(passwordData);
      if (response.success || response.status === 800) {
        toast.success('Password changed successfully!');
        return response;
      } else {
        throw new Error(response.message || 'Failed to change password');
      }
    } catch (err) {
      const message = err.message || 'Failed to change password';
      setError(message);
      toast.error(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (profileData) => {
    setLoading(true);
    setError(null);
    try {
      const userId = user?.id || localStorage.getItem(STORAGE_KEYS.USER_ID);
      if (!userId) throw new Error('User ID not found');
      
      const response = await userApi.updateProfile(userId, profileData);
      
      if (response.success || response.status === 800) {
        const updatedUser = { ...user, ...profileData };
        setUser(updatedUser);
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(updatedUser));
        toast.success('Profile updated successfully!');
        return response;
      } else {
        throw new Error(response.message || 'Failed to update profile');
      }
    } catch (err) {
      const message = err.message || 'Failed to update profile';
      setError(message);
      toast.error(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteAccountRequest = async (data) => {
    setLoading(true);
    try {
      const response = await userApi.requestDeleteOtp(data);
      return response;
    } catch (err) {
      toast.error(err.message || 'Failed to request deletion OTP');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteAccountVerify = async (data) => {
    setLoading(true);
    try {
      const response = await userApi.deleteAccount(data);
      if (response.success || response.status === 800) {
        logout();
      }
      return response;
    } catch (err) {
      toast.error(err.message || 'Failed to delete account');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const switchEvent = (event) => {
    setCurrentEvent(event);
    localStorage.setItem('selectedEventId', event.id?.toString() || event.event_address);
    localStorage.setItem(STORAGE_KEYS.UID, event.event_address);
    localStorage.setItem(STORAGE_KEYS.EVENT_ID, event.id);
    
    // Hard refresh so all components & state reflect the newly selected event
    window.location.reload();
  };

  return {
    user,
    events,
    currentEvent,
    loading,
    error,
    login,
    logout,
    switchEvent,
    setDefaultEvent,
    changePassword,
    updateProfile,
    fetchProfile,
    deleteAccountRequest,
    deleteAccountVerify,
    isAuthenticated: !!user,
    isAdmin: user?.role === 1,
    isUser: user?.role === 0,
    updateUser: setUser
  };
};
